import { Location } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, filter, ReplaySubject, Subject, take, takeUntil } from 'rxjs';

import { AppState } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import Social from 'entity/Social';
import { IncomingMessage, IncomingMessageType } from 'messages/websocket.messages';
import SocialController from 'rest/SocialController';

import { LeaveRoomType } from '../../social-leave-room/social-leave-room-type';
import { socialAppActions, socialAppSelectors } from '../../state/app';
import { ChatService } from '../chat.service';
import { MonitoringService } from '../monitor/monitoring.service';
import { SceneService } from '../scene.service';
import { GroupShareScreenParticipantService } from '../share-screen-group/group-share-screen-participant.service';
import { VideoChatService } from '../videochat.service';
import { WebsocketService } from '../websocket.service';

/**
 * Service to handle the user's presence in the room
 *
 * This service is responsible for:
 * - Joining the room
 * - Leaving the room
 * - Switching rooms
 * - Events around these actions
 */
@Injectable({
  providedIn: 'root',
})
export class RoomPresenceService extends Loggable {
  private store = inject<Store<AppState>>(Store);
  private websocketService = inject(WebsocketService);
  private videoChatService = inject(VideoChatService);
  private monitoringService = inject(MonitoringService);
  private chatService = inject(ChatService);
  private location = inject(Location);
  private groupShareScreenParticipantService = inject(GroupShareScreenParticipantService);

  private presentInRoom = false;
  private roomSetId?: string;
  private social?: Social;
  private readonly socialController = new SocialController();

  private waitingForHostStepSource = new BehaviorSubject<string | undefined>(undefined); // Initial value
  public waitingForHostStep$ = this.waitingForHostStepSource.asObservable();

  public leaveRoomSubject = new Subject<LeaveRoomType>();
  public leaveRoom$ = this.leaveRoomSubject.asObservable();

  private destroyed$ = new ReplaySubject<boolean>(1);

  public constructor() {
    super();
    this.setDisplayName('RoomPresenceService');
  }

  public init() {
    this.destroyed$ = new ReplaySubject(1);
    this.store
      .select(socialAppSelectors.isJoined)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isJoined) => (this.presentInRoom = isJoined));

    this.store
      .select(socialAppSelectors.getCurrentSocial)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((social) => {
        this.social = social;
      });

    this.store
      .select(socialAppSelectors.getCurrentConnectedRoomSetId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((setId) => (this.roomSetId = setId));

    // If the user is banned, kick them out
    this.store
      .select(socialAppSelectors.isBannedUser)
      .pipe(
        filter((isBanned) => isBanned),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => this.handleBannedUser());

    this.websocketService.message$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((message) => this.handleWebsocketMessage(message));

    // this.leaveRoom$.pipe(takeUntil(this.destroyed$)).subscribe(() => this.handleLeaveRoom());
  }

  public destroy() {
    this.destroyed$ = new ReplaySubject(1);
  }

  public joinRoom(): void {
    this.monitoringService.initialize();
  }

  /**
   * Leave the room (exit without closing)
   */
  public leaveRoom(): void {
    const type = this.presentInRoom ? LeaveRoomType.LEAVE_ROOM : LeaveRoomType.LEAVE_UNJOINED_ROOM;
    this.info('Leaving room', type);
    this.leaveRoomSubject.next(type);
  }

  public closeWindow(): void {
    const type = LeaveRoomType.CLOSE_WINDOW;
    this.info('Close window', type);
    this.leaveRoomSubject.next(type);
  }

  // private handleLeaveRoom() {
  // Log ChurnZero Event Leave Room and number of participants still in room
  // const { sessionId, socialId: roomId } = this.social ?? {};
  // const { participantCount } = this.sceneService;
  // const customFields: ChurnZeroCustomFields = {
  //   'Current Number of Users in Room': participantCount.toString(),
  //   RoomID: roomId ?? '',
  //   ScootaverseID: this.roomSetId ?? '',
  //   SessionID: sessionId ?? '',
  // };
  // this.churnZeroService.trackEvent(ChurnZeroEvent.LEAVE_ROOM, customFields);
  // }

  /**
   * Close the room set.
   */
  public closeRoom(): void {
    this.info('Closing room', { roomSetId: this.roomSetId });
    const roomSetId = this.roomSetId;
    if (!roomSetId) {
      this.error('closeRoom: No room set id found');
    } else {
      this.socialController.closeSocialRoomSet(roomSetId).catch((e) => {
        this.error(`closeRoom: Failed to close room set ${roomSetId}`, e);
      });
    }

    this.leaveRoomSubject.next(LeaveRoomType.LEAVE_AND_CLOSE_ROOM);
  }

  /**
   * Change to another room in the connected room set.
   * @param roomId The room id to change to
   */
  public changeRoom(roomId: string): void {
    this.info('Changing room', { roomId });
    this.videoChatService.getSocial(roomId).subscribe((room) => {
      //Avoid events where this.roomSetId is undefined
      const roomSetId = this.roomSetId ? this.roomSetId : room.socialRoomSetId;
      //If is undefined, set it to the roomSetId of the room we are changing to
      if (!this.roomSetId) {
        this.roomSetId = roomSetId;
      }

      this.store.dispatch(socialAppActions.SELECT_CONNECTED_ROOM({ social: room }));
      this.location.replaceState(`connected-room/${roomSetId}/${room.socialId}`);
      // Clear services when user leaves room
      this.chatService.clear();
      this.groupShareScreenParticipantService.clear();
    });
  }

  /**
   * Change to another room while in waiting room
   * @param roomId The room id to change to
   */
  public changeInitialRoom(roomId: string): void {
    this.info('Changing initial room', { roomId });
    this.videoChatService
      .getSocial(roomId)
      .pipe(take(1))
      .subscribe((room) => {
        this.store.dispatch(socialAppActions.SELECT_CONNECTED_ROOM({ social: room }));
        this.location.replaceState(`connected-room/${this.roomSetId}/${room.socialId}`);
        return true;
      });
    return;
  }

  private handleWebsocketMessage(msg: IncomingMessage): void {
    const { t: type } = msg;

    switch (type) {
      case IncomingMessageType.PARTICIPANT_COUNT_UPDATE: {
        const { count, socialId } = msg;
        this.store.dispatch(socialAppActions.UPDATE_CONNECTED_ROOM_COUNT({ updatedRoomCounts: { [socialId]: count } }));
        return;
      }

      case IncomingMessageType.REMOVE_JOINED_ANOTHER: {
        this.handleUserJoinedAnotherRoom();
      }
    }
  }

  private handleUserJoinedAnotherRoom(): void {
    this.info('Leaving room. User joined another room');
    this.leaveRoomSubject.next(LeaveRoomType.LEAVE_JOIN_ANOTHER);
  }

  /**
   * If the user is banned, kick them out
   *
   * FIXME: This should be private, but a few components directly ask for the user's banned status.
   */
  public handleBannedUser(): void {
    this.info('Leaving room. User is banned');
    this.leaveRoomSubject.next(LeaveRoomType.LEAVE_BANNED_USER);
  }

  // Handle user step when in waiting for host
  public handleWaitingForHostStep(step?: string): void {
    this.waitingForHostStepSource.next(step);
  }
}
